import React from 'react';
import { motion } from 'framer-motion';

const Conditions = () => {
  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="conditions-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="text-sm uppercase tracking-[0.2em] text-[var(--color-primary)] font-medium">
            Legal
          </span>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter text-[var(--text-primary)] mt-4 mb-4">
            Terms and Conditions
          </h1>
          <p className="text-[var(--text-secondary)] mb-8">
            Last updated: January 15, 2026
          </p>

          <div className="space-y-8 text-[var(--text-secondary)]">
            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">1. Service Agreement</h2>
              <p>
                This Agreement constitutes a legally binding contract between you and DeFi Bot Ltd. ("Company") regarding your use of the DeFi Bot automated trading platform ("Platform"). By using the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">2. Eligibility</h2>
              <p>To use our Service, you must:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Be at least 18 years of age or the legal age in your jurisdiction</li>
                <li>Have the legal capacity to enter into binding contracts</li>
                <li>Not be located in a jurisdiction where cryptocurrency trading is prohibited</li>
                <li>Not be on any sanctions list or blocked persons list</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">3. Account Registration</h2>
              <p>
                To access certain features of the Platform, you must connect a compatible cryptocurrency wallet. You are responsible for:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Maintaining the confidentiality of your wallet credentials</li>
                <li>All activities that occur through your connected wallet</li>
                <li>Notifying us immediately of any unauthorized access</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">4. Subscription Plans</h2>
              <p>
                DeFi Bot offers various subscription tiers with different features. All subscriptions:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Are one-time payments providing lifetime access</li>
                <li>Include access to features available at the time of purchase</li>
                <li>May be upgraded at any time by paying the difference</li>
                <li>Are non-refundable once payment is confirmed</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">5. Trading Risks</h2>
              <p className="mb-4">
                <strong className="text-[var(--accent-danger)]">WARNING:</strong> Cryptocurrency trading involves substantial risk of loss and is not suitable for every investor. You acknowledge and agree that:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Cryptocurrency markets are highly volatile and unpredictable</li>
                <li>You may lose some or all of your invested capital</li>
                <li>Past performance is not indicative of future results</li>
                <li>The Company does not guarantee any returns or profits</li>
                <li>You are solely responsible for your trading decisions</li>
                <li>Automated trading bots can malfunction or produce unexpected results</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">6. Prohibited Activities</h2>
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Use the Platform for any illegal purposes</li>
                <li>Attempt to manipulate markets or engage in wash trading</li>
                <li>Reverse engineer, decompile, or disassemble any part of the Platform</li>
                <li>Circumvent any security measures or access controls</li>
                <li>Share your subscription with unauthorized third parties</li>
                <li>Use the Platform to facilitate money laundering or terrorist financing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">7. Intellectual Property</h2>
              <p>
                All content, features, and functionality of the Platform are owned by the Company and protected by international copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works without our express written permission.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">8. Disclaimer of Warranties</h2>
              <p>
                THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. THE COMPANY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">9. Limitation of Liability</h2>
              <p>
                IN NO EVENT SHALL THE COMPANY BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, USE, OR OTHER INTANGIBLE LOSSES, REGARDLESS OF WHETHER THE COMPANY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">10. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless the Company and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Platform or violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">11. Dispute Resolution</h2>
              <p>
                Any disputes arising from these Terms shall be resolved through binding arbitration in London, United Kingdom, in accordance with the LCIA Arbitration Rules. The arbitration shall be conducted in English.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">12. Modifications</h2>
              <p>
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting. Your continued use of the Platform constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">13. Contact Information</h2>
              <p>
                For questions or concerns regarding these Terms, please contact:
              </p>
              <p className="mt-2">
                DeFi Bot Ltd.<br />
                71-75 Shelton Street<br />
                Covent Garden, London WC2H 9JQ<br />
                United Kingdom<br />
                Email: legal@defi-bot.io
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Conditions;
